/**
 * Who is using the application, which tenant they are a courier for, and which courier they are.
 *
 * All three are read from the access token and never chosen by the application. The scopes decide
 * what is drawn, the tenant decides what is asked for, and a client that let either be set locally
 * would be deciding its own authorization — which `apps/README.md` forbids and which every service
 * would refuse anyway.
 *
 * ## The one thing this file must not assert, and does not
 *
 * EXP-003's three operations are addressed by `driverId`, and that is **not** known to be the
 * token's subject. R-10 measured it: DRV-01 mints `driverId` with `randomUUID()` and keeps it
 * separate from the `userId` that is the identity subject, and nothing on this platform states that
 * the two are the same value. DSP-02 nevertheless compares an offer's `candidateRef.driverId`
 * against the token's subject, so either those checks refuse every real courier or the identifier is
 * a subject — and nobody has decided which. It is pinned in `docs/OPEN_WORK.md`.
 *
 * EXP-003 deliberately takes the `driverId` from its path and never derives one from a token, "never
 * compared against the token's subject here" in its own contract, because publishing that
 * equivalence would make it true by accident. This application must not publish it either. So:
 *
 *   - `driverId` comes from a `driver_id` claim when the realm issues one, which is the only place
 *     a token could state it;
 *   - otherwise it is **absent**, and the screens say they do not know which courier this is rather
 *     than sending the subject and hoping.
 *
 * Sending `sub` as the `driverId` is the convenient thing and would make every screen appear to
 * work on a realm where the two happen to coincide, and silently read somebody else's readiness on
 * one where they do not. Recorded as R-29.
 */

export interface Session {
  readonly subject: string;
  readonly displayName: string;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  /**
   * The courier this session is for, where the token says so.
   *
   * Undefined when the realm issues no `driver_id` claim. Not a fallback to guess around: a
   * readiness screen addressed with the wrong identifier is somebody else's readiness.
   */
  readonly driverId?: string;
  /** What the design's own sample calls the courier, when the token carries a name. */
  readonly initials: string;
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Two letters: the first of the first and last words, or the first two of a single word. */
function initialsOf(displayName: string): string {
  const words = displayName
    .split(/[\s._-]+/)
    .map((word) => word.trim())
    .filter((word) => /^[A-Za-z]/.test(word));
  if (words.length === 0) return displayName.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words.at(-1)![0]}`.toUpperCase();
}

interface AccessTokenClaims {
  readonly sub?: unknown;
  readonly name?: unknown;
  readonly scope?: unknown;
  readonly tenant_id?: unknown;
  readonly driver_id?: unknown;
}

/**
 * Read a session out of an access token, without trusting it for anything but display and addressing.
 *
 * The signature is verified by the services, not here — a browser cannot hold the key, and a client
 * that "validated" a token would be performing a check whose failure it would also be responsible
 * for reporting. What this does is read the claims so the application knows which courier to ask
 * about and what it may ask for, and every request still carries the token to a service that checks
 * it properly.
 */
export function sessionFromClaims(claims: AccessTokenClaims): Session | undefined {
  const subject = typeof claims.sub === "string" ? claims.sub : undefined;
  const tenantId = typeof claims.tenant_id === "string" ? claims.tenant_id : undefined;
  if (subject === undefined || tenantId === undefined) return undefined;

  const scope = claims.scope;
  const scopes = Array.isArray(scope)
    ? scope.filter((value): value is string => typeof value === "string")
    : typeof scope === "string"
      ? scope.split(" ").filter((value) => value.length > 0)
      : [];

  const displayName =
    typeof claims.name === "string" && claims.name.length > 0 ? claims.name : subject;

  /*
   * A `driver_id` that is not a uuid is treated as absent.
   *
   * Every identifier EXP-003's path accepts is `format: uuid` and its route refuses anything else
   * with a 400, so a malformed claim would produce a refusal on every screen with no clue as to why.
   * Absent at least says which fact is missing.
   */
  const driverId =
    typeof claims.driver_id === "string" && UUID.test(claims.driver_id)
      ? claims.driver_id
      : undefined;

  return {
    subject,
    displayName,
    scopes: new Set(scopes),
    tenantId,
    ...(driverId === undefined ? {} : { driverId }),
    initials: initialsOf(displayName),
  };
}

/** The two scopes EXP-003's operations enforce, so a screen can say which one it is missing. */
export const READ_SCOPE = "driver-app:read";
export const STREAM_SCOPE = "driver-app:stream";
